/**
 * API Base URL resolver for runtime environments
 *
 * P20251002-PREVIEW-SAME-ORIGIN-AUTH
 *
 * Strategy:
 * - Lovable preview (*.lovable.dev) + localhost: Use SAME-ORIGIN (relative URLs, no cross-site cookies)
 * - Production (attaindomains.com): Use PUBLIC_ORIGIN if set, else same-origin
 *
 * This avoids cross-site cookie blocking in preview environments.
 */

/**
 * Get the API base URL for the current runtime environment
 * @returns Empty string for same-origin, or absolute URL for cross-origin
 */
export function getApiBase(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;

    // Preview environments: use same-origin (relative URLs)
    if (
      /\.(lovable\.dev|lovable\.run)$/.test(host) ||
      host === 'localhost' ||
      host === '127.0.0.1'
    ) {
      return '';
    }

    // Allow explicit override from env (for staging/custom deployments)
    const override = import.meta?.env?.PUBLIC_ORIGIN;
    if (override && typeof override === 'string') {
      return override.replace(/\/+$/, '');
    }
  }

  // Production: same-origin (empty string)
  return '';
}

/**
 * Join API base with a path, ensuring proper slash handling
 * @param path - API path starting with /
 * @returns Full URL or relative path
 */
export function joinApi(path: string): string {
  const base = getApiBase();
  if (!base) return path; // Same-origin

  // Ensure single slash between base and path
  const cleanBase = base.replace(/\/+$/, '');
  const cleanPath = path.replace(/^\/+/, '');
  return `${cleanBase}/${cleanPath}`;
}
